/*
 * search.js — Agent D (spec §6-D)
 * ───────────────────────────────────────────────────────────────────────────
 * 1) Predictive search via Section Rendering API.
 *    Fyller headerns <div data-predictive-results> (markup-kontrakt med G2).
 *    - debounce 200 ms, abortar tidigare fetch
 *    - SKU-träffar via resources[options][fields]=...,variants.sku,...
 *    - priser kommer FÄRDIGFORMATERADE från snippeten (spec §7 bugg 5)
 *    - piltangentnavigering (↑/↓/Enter), Escape stänger, klick utanför stänger
 *    - tomt q → stäng (ingen fetch)
 * 2) Kollektionsfacetter: AJAX-uppdatering av grid + facetter via section_id,
 *    history.pushState, mobil filter-drawer, auto-submit av sortering.
 *    No-JS GET-fallback finns i markup; JS förbättrar bara.
 * 3) Sökresultatsidans sortering auto-submittar.
 *
 * IIFE, vanilla ES2019, inga beroenden. Idempotent (kör en gång).
 */
(function () {
  'use strict';
  if (window.__jlSearchInit) return;
  window.__jlSearchInit = true;

  var PREDICTIVE_SECTION = 'predictive-search';
  var DEBOUNCE_MS = 200;
  var MIN_CHARS = 2;

  /* ───────────────────────── Hjälpare ───────────────────────── */

  function suggestEndpoint() {
    /* Agent G:s kontrakt: window.JolatConfig.routes.predictiveSearch
       (= routes.predictive_search_url, hanterar lokaliserade rötter som /sv). */
    var cfg = window.JolatConfig && window.JolatConfig.routes;
    if (cfg && cfg.predictiveSearch) return cfg.predictiveSearch;
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (base.charAt(base.length - 1) !== '/') base += '/';
    return base + 'search/suggest';
  }

  function predictiveUrl(term) {
    var params = [
      'q=' + encodeURIComponent(term),
      'resources[type]=product,page',
      'resources[limit]=6',
      'resources[options][unavailable_products]=last',
      'resources[options][fields]=title,product_type,variants.sku,vendor,tag',
      'section_id=' + PREDICTIVE_SECTION
    ];
    var endpoint = suggestEndpoint();
    return endpoint + (endpoint.indexOf('?') === -1 ? '?' : '&') + params.join('&');
  }

  /* Plockar ut [data-predictive-panel] ur en renderad sektion */
  function extractPanel(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var panel = doc.querySelector('[data-predictive-panel]');
    return panel ? panel.outerHTML : html;
  }

  /* ───────────────────── Predictive search ───────────────────── */

  function PredictiveGroup(form) {
    this.form = form;
    this.input = form.querySelector('[data-predictive-input]');
    this.results = form.querySelector('[data-predictive-results]');
    if (!this.input || !this.results) return;
    this.controller = null;
    this.timer = null;
    this.lastTerm = '';
    this.open = false;
    this.bind();
  }

  PredictiveGroup.prototype.bind = function () {
    var self = this;

    this.input.addEventListener('input', function () {
      var term = self.input.value.trim();
      window.clearTimeout(self.timer);
      if (term.length < MIN_CHARS) { self.close(); return; }
      self.timer = window.setTimeout(function () { self.search(term); }, DEBOUNCE_MS);
    });

    this.input.addEventListener('focus', function () {
      var term = self.input.value.trim();
      if (term.length >= MIN_CHARS && self.results.children.length) self.show();
    });

    this.input.addEventListener('keydown', function (e) { self.onKeydown(e); });

    /* Klick i resultat följer länken — låt det ske; men klick utanför stänger */
    document.addEventListener('click', function (e) {
      if (!self.open) return;
      if (self.form.contains(e.target)) return;
      self.close();
    });
  };

  PredictiveGroup.prototype.search = function (term) {
    var self = this;
    if (self.controller) self.controller.abort();
    self.controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var opts = { headers: { Accept: 'text/html' } };
    if (self.controller) opts.signal = self.controller.signal;

    fetch(predictiveUrl(term), opts)
      .then(function (r) {
        if (!r.ok) throw new Error('predictive ' + r.status);
        return r.text();
      })
      .then(function (html) {
        self.lastTerm = term;
        self.results.innerHTML = extractPanel(html);
        /* Låt momsläget slå igenom på de färdigformaterade priserna (spec §4/§7 bugg 5) */
        if (window.Jolat && window.Jolat.refreshPrices) window.Jolat.refreshPrices(self.results);
        self.show();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        self.close();
      });
  };

  PredictiveGroup.prototype.show = function () {
    this.results.hidden = false;
    this.open = true;
    this.input.setAttribute('aria-expanded', 'true');
    this.activeIndex = -1;
  };

  PredictiveGroup.prototype.close = function () {
    if (!this.results) return;
    this.results.hidden = true;
    this.open = false;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.clearActive();
    this.activeIndex = -1;
  };

  PredictiveGroup.prototype.options = function () {
    return Array.prototype.slice.call(this.results.querySelectorAll('[data-psr-option]'));
  };

  PredictiveGroup.prototype.clearActive = function () {
    this.options().forEach(function (o) { o.classList.remove('is-active'); o.removeAttribute('aria-selected'); });
  };

  PredictiveGroup.prototype.setActive = function (idx) {
    var opts = this.options();
    if (!opts.length) return;
    this.clearActive();
    if (idx < 0) idx = opts.length - 1;
    if (idx >= opts.length) idx = 0;
    this.activeIndex = idx;
    var el = opts[idx];
    el.classList.add('is-active');
    el.setAttribute('aria-selected', 'true');
    if (!el.id) el.id = 'jl-psr-opt-' + idx;
    this.input.setAttribute('aria-activedescendant', el.id);
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  };

  PredictiveGroup.prototype.onKeydown = function (e) {
    if (e.key === 'Escape') {
      if (this.open) { e.stopPropagation(); this.close(); }
      return;
    }
    if (!this.open) return;
    var opts = this.options();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setActive(this.activeIndex < 0 ? 0 : this.activeIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setActive(this.activeIndex < 0 ? opts.length - 1 : this.activeIndex - 1);
    } else if (e.key === 'Enter') {
      if (this.activeIndex >= 0 && opts[this.activeIndex]) {
        e.preventDefault();
        opts[this.activeIndex].click();
      }
      /* annars: låt formuläret submitta till sökresultatsidan */
    }
  };

  function initPredictive() {
    document.querySelectorAll('[data-predictive-search-form]').forEach(function (form) {
      if (form.__jlPredictive) return;
      form.__jlPredictive = new PredictiveGroup(form);
    });
  }

  /* ───────────────────── Kollektionsfacetter ───────────────────── */

  function FacetController(root) {
    this.root = root;
    this.form = root.querySelector('[data-facet-form]');
    this.sectionId = root.getAttribute('data-section-id');
    if (!this.form) return;
    this.gridRegion = root.querySelector('[data-facet-region="grid"]');
    this.filterRegion = root.querySelector('[data-facet-region="filters"]');
    this.controller = null;
    this.bind();
  }

  FacetController.prototype.bind = function () {
    var self = this;

    /* Checkbox/prisfält ändras → submit (AJAX) */
    this.form.addEventListener('change', function (e) {
      if (e.target.matches('input[type="checkbox"], select[name="sort_by"]')) {
        self.apply();
      }
    });

    /* Prisfält: submit vid Enter, annars på blur via change */
    this.form.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('input[type="number"]')) {
        e.preventDefault();
        self.apply();
      }
    });

    /* Chips, "Rensa alla", tom-state-länk → AJAX-navigering */
    this.root.addEventListener('click', function (e) {
      var link = e.target.closest('[data-facet-link]');
      if (link && self.root.contains(link)) {
        e.preventDefault();
        self.navigate(link.getAttribute('href'));
        return;
      }
    });

    /* Mobil drawer */
    this.root.addEventListener('click', function (e) {
      if (e.target.closest('[data-facet-open]')) { e.preventDefault(); self.openDrawer(); return; }
      if (e.target.closest('[data-facet-close]')) { e.preventDefault(); self.closeDrawer(); return; }
      if (e.target.closest('[data-facet-show]')) { e.preventDefault(); self.closeDrawer(); }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.root.getAttribute('data-drawer-open') === 'true') self.closeDrawer();
    });

    /* Bakåt/framåt i webbläsaren */
    window.addEventListener('popstate', function () {
      self.fetchAndRender(window.location.href, false);
    });
  };

  FacetController.prototype.openDrawer = function () {
    this.root.setAttribute('data-drawer-open', 'true');
    document.documentElement.classList.add('jl-noscroll');
    this.root.querySelectorAll('[data-facet-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    var panel = this.root.querySelector('[data-facet-panel]');
    if (panel) {
      var close = panel.querySelector('[data-facet-close]');
      if (close) window.setTimeout(function () { close.focus(); }, 60);
    }
  };

  FacetController.prototype.closeDrawer = function () {
    this.root.setAttribute('data-drawer-open', 'false');
    document.documentElement.classList.remove('jl-noscroll');
    this.root.querySelectorAll('[data-facet-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
  };

  FacetController.prototype.apply = function () {
    var qs = new URLSearchParams(new FormData(this.form));
    /* Töm tomma prisfält så de inte ligger kvar i URL:en */
    var clean = new URLSearchParams();
    qs.forEach(function (val, key) {
      if (val !== '' && val != null) clean.append(key, val);
    });
    var url = this.form.getAttribute('action') + '?' + clean.toString();
    this.navigate(url);
  };

  FacetController.prototype.navigate = function (url) {
    this.fetchAndRender(url, true);
  };

  FacetController.prototype.fetchAndRender = function (url, pushState) {
    var self = this;
    if (self.controller) self.controller.abort();
    self.controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;

    var u = new URL(url, window.location.origin);
    var fetchUrl = new URL(u.href);
    fetchUrl.searchParams.set('section_id', self.sectionId);

    self.root.setAttribute('data-loading', 'true');
    var opts = { headers: { Accept: 'text/html' } };
    if (self.controller) opts.signal = self.controller.signal;

    fetch(fetchUrl.href, opts)
      .then(function (r) {
        if (!r.ok) throw new Error('facet ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        self.swap(doc, '[data-facet-region="grid"]');
        self.swap(doc, '[data-facet-region="filters"]');
        self.syncMeta(doc);
        if (pushState) window.history.pushState({ jlFacet: true }, '', u.href);
        self.root.removeAttribute('data-loading');
        /* Nyinjicerade produktkort: applicera aktuellt momsläge/pro-pris */
        if (window.Jolat && window.Jolat.refreshPrices) window.Jolat.refreshPrices(self.gridRegion || self.root);
        self.scrollIntoView();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        self.root.removeAttribute('data-loading');
        /* Hård fallback: full navigering */
        window.location.href = url;
      });
  };

  FacetController.prototype.swap = function (doc, selector) {
    var fresh = doc.querySelector(selector);
    var current = this.root.querySelector(selector);
    if (!fresh || !current) return;

    /* Bevara öppet/stängt-läge på facettgrupper (spec §6-D / facet-filters-kontrakt) */
    var openState = {};
    current.querySelectorAll('details[data-facet-key]').forEach(function (d) {
      openState[d.getAttribute('data-facet-key')] = d.open;
    });

    current.innerHTML = fresh.innerHTML;

    current.querySelectorAll('details[data-facet-key]').forEach(function (d) {
      var key = d.getAttribute('data-facet-key');
      if (Object.prototype.hasOwnProperty.call(openState, key)) d.open = openState[key];
    });
  };

  /* Synka antalsetiketter, badge och "Visa N produkter"-knapp */
  FacetController.prototype.syncMeta = function (doc) {
    var pairs = [
      ['[data-facet-count]', '[data-facet-count]'],
      ['[data-facet-show]', '[data-facet-show]'],
      ['[data-facet-badge]', '[data-facet-badge]']
    ];
    var self = this;
    pairs.forEach(function (sel) {
      var fresh = doc.querySelector(sel[0]);
      var current = self.root.querySelector(sel[1]);
      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
        if (fresh.hasAttribute('hidden')) { current.setAttribute('hidden', ''); }
        else { current.removeAttribute('hidden'); }
      }
    });
  };

  FacetController.prototype.scrollIntoView = function () {
    var rect = this.root.getBoundingClientRect();
    if (rect.top < 0) window.scrollTo({ top: window.scrollY + rect.top - 12, behavior: 'auto' });
  };

  function initFacets() {
    document.querySelectorAll('[data-facet-root]').forEach(function (root) {
      if (root.__jlFacets) return;
      root.__jlFacets = new FacetController(root);
    });
  }

  /* ───────────────────── Sökresultatsidans sortering ───────────────────── */

  function initSearchSort() {
    document.querySelectorAll('[data-search-sort]').forEach(function (sel) {
      if (sel.__jlBound) return;
      sel.__jlBound = true;
      sel.addEventListener('change', function () {
        var form = sel.closest('[data-search-sort-form]') || sel.form;
        if (form) form.submit();
      });
    });
  }

  /* ───────────────────────── Init ───────────────────────── */

  function init() {
    initPredictive();
    initFacets();
    initSearchSort();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
